import React, { useEffect, useState } from "react";
import { View, ScrollView, StyleSheet, TouchableOpacity } from "react-native";
import { useLocalSearchParams, router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import ThemedInput from "@/components/ThemedInput";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { useModal } from "@/hooks/useModal";
import { ConfirmModal } from "@/components/Modals/ConfirmModal";
import { getEntityPermissions } from "@/lib/permissions";
import { roleService } from "@/services/roleService";
import { userService } from "@/services/userService";
import { supabase } from "@/lib/supabase-client";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { UserPicker } from "@/components/Pickers/UserPicker";
import { PhonePicker } from "@/components/Pickers/PhonePicker";
import { PermissionsSchema, RoleType } from "@/types/db/roles";

// Role data interface
export interface RoleData {
  id: string;
  name: string;
  description?: string;
  meta_data?: {
    permissions?: any;
    priority?: number;
    accept_shifts?: boolean;
  };
  roles_users?: { user_id: string; tenant_id: string }[];
}

// Helper function to format field names for display
const formatLabel = (key: string): string => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Permission metadata type
type PermissionMeta = { key: string; label: string; description: string; childOf?: string; displayNameKey?: string };

// Map permission keys to displayNames keys (for entities that have display names)
const PERMISSION_TO_DISPLAY_NAME: Record<string, string> = {
  projects: "projects",
  shifts: "shifts",
  shift_users: "shifts",
  shift_positions: "shifts",
  time_cards: "time_cards",
  users: "users",
  user_abilities: "users",
  user_positions: "users",
  user_restrictions: "users",
  user_private: "users",
  user_pay: "users",
  user_reviews: "users",
  user_emergency_contacts: "users",
  invoices: "invoices",
  estimates: "estimates",
  expenses: "expenses",
  incidents: "incidents",
  investigations: "investigations",
  contacts: "contacts",
  roles: "roles",
  courses: "courses",
  applications: "applications",
  clients: "clients",
  payroll: "payroll",
};

// Extract permission metadata from schema
const getPermissionMetadata = () => {
  // Unwrap the default wrapper to access the object shape
  const innerSchema = (PermissionsSchema as any)._def.innerType;
  const shape = innerSchema.shape;
  const booleanPerms: PermissionMeta[] = [];
  const crudPerms: PermissionMeta[] = [];
  const updateOnlyPerms: PermissionMeta[] = [];

  Object.keys(shape).forEach((key) => {
    const field = shape[key as keyof typeof shape];
    const description = (field as any)._def?.description || "";
    const childOf = (field as any)._def?.ui?.childOf;

    // Skip phones array
    if (key === "phones") return;

    // Check if it's a ZodDefault wrapper
    if ((field as any)._def?.typeName === "ZodDefault") {
      const innerType = (field as any)._def?.innerType;

      const displayNameKey = PERMISSION_TO_DISPLAY_NAME[key];

      // Check if the inner type is a boolean
      if (innerType?._def?.typeName === "ZodBoolean") {
        booleanPerms.push({ key, label: formatLabel(key), description, childOf, displayNameKey });
      }
      // Check if it's an object (CRUD or update-only)
      else if (innerType?._def?.typeName === "ZodObject") {
        const innerShape = innerType.shape || innerType._def?.shape?.();
        if (innerShape?.create !== undefined) {
          // It's a CRUD permission
          crudPerms.push({ key, label: formatLabel(key), description, childOf, displayNameKey });
        } else if (innerShape?.update !== undefined && !innerShape?.create) {
          // It's an update-only permission (like integrations, tenant)
          updateOnlyPerms.push({ key, label: formatLabel(key), description, childOf, displayNameKey });
        }
      }
    }
  });

  return { booleanPerms, crudPerms, updateOnlyPerms };
};

const {
  booleanPerms: BOOLEAN_PERMISSIONS,
  crudPerms: CRUD_PERMISSION_CATEGORIES,
  updateOnlyPerms: UPDATE_ONLY_PERMISSIONS,
} = getPermissionMetadata();

// Helper to get display label for a permission
const getPermissionLabel = (perm: PermissionMeta, displayNames: any): { label: string; originalLabel?: string } => {
  if (!perm.displayNameKey || !displayNames?.[perm.displayNameKey]) {
    return { label: perm.label };
  }

  const displayName = displayNames[perm.displayNameKey];
  // Use plural for CRUD permissions (managing multiple items)
  const customName = displayName.plural;
  const defaultName = perm.label;

  // For child permissions, replace parent name prefix with custom name
  // e.g., "Shift Users" -> "Gig Users" if shifts is renamed to Gigs
  if (perm.childOf && perm.displayNameKey) {
    const parentDefault = formatLabel(perm.childOf);
    const parentCustom = displayNames[perm.displayNameKey]?.plural;
    if (parentCustom && parentCustom !== parentDefault && perm.label.startsWith(parentDefault.split(" ")[0])) {
      const newLabel = perm.label.replace(parentDefault.split(" ")[0], parentCustom.replace(/s$/, ""));
      if (newLabel !== perm.label) {
        return { label: newLabel, originalLabel: perm.label };
      }
    }
    return { label: perm.label };
  }

  // For parent permissions, use the display name directly
  if (customName !== defaultName) {
    return { label: customName, originalLabel: defaultName };
  }

  return { label: perm.label };
};

export default function RoleEditPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const { userRoles, tenantId, session, displayNames } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleData, setRoleData] = useState<RoleData | null>(null);
  const [editedRole, setEditedRole] = useState<RoleData | null>(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "users" | "permissions" | "phones">("overview");

  const [users, setUsers] = useState<any[]>([]);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [showPhonePicker, setShowPhonePicker] = useState(false);
  const [twilioConfigured, setTwilioConfigured] = useState<boolean | null>(null);

  const permissions = getEntityPermissions(userRoles, "roles");

  // Check Twilio configuration
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("twilio-api-status", {
          body: {},
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        setTwilioConfigured(Boolean(data?.hasTwilio ?? data?.accountSid));
      } catch {
        setTwilioConfigured(false);
      }
    })();
  }, [session?.access_token]);

  // Load role data
  useEffect(() => {
    if (id) {
      loadRoleData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadRoleData = async () => {
    try {
      setLoading(true);
      const role = (await roleService.getRoleById(id!)) as RoleData & { roles_users?: any[] };
      setRoleData(role);
      setEditedRole(role);

      // Load users from roles_users link table (batch to avoid URL length issues)
      const userIds = (role.roles_users || []).map((ru) => ru.user_id);
      if (userIds.length > 0) {
        const BATCH_SIZE = 50;
        const batches: string[][] = [];
        for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
          batches.push(userIds.slice(i, i + BATCH_SIZE));
        }
        const results = await Promise.all(batches.map((batch) => userService.getUsers({ userIds: batch })));
        setUsers(results.flat());
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      toast.showToast({
        title: "Failed to load role",
        message: error?.message || "Unable to load role data",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editedRole || !id || !tenantId) return;

    setSaving(true);
    try {
      // Save role data (name, description, permissions) - strip out roles_users relation
      const { roles_users, ...roleDataToSave } = editedRole;
      await roleService.saveRowPatches([roleDataToSave as Partial<RoleType>]);

      // Sync users in link table
      const userIds = users.map((u) => u.id!);
      await roleService.syncRoleUsers(id, tenantId, userIds);

      setRoleData(editedRole);
      setEditing(false);

      toast.showToast({
        title: "Role updated",
        message: "Role has been successfully updated",
        toastType: "success",
      });

      await loadRoleData();
    } catch (error: any) {
      toast.showToast({
        title: "Update failed",
        message: error?.message || "Failed to update role",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedRole(roleData);
    setEditing(false);
  };

  const handlePermissionToggle = (category: string, action: string, value: boolean) => {
    if (!editedRole) return;

    const currentPermissions = editedRole.meta_data?.permissions || {};

    if (action === "boolean") {
      // Handle boolean permissions like admin_portal and integrations
      setEditedRole({
        ...editedRole,
        meta_data: {
          ...editedRole.meta_data,
          permissions: {
            ...currentPermissions,
            [category]: value,
          },
        },
      });
    } else {
      // Handle CRUD permissions
      const categoryPerms = currentPermissions[category] || {};
      setEditedRole({
        ...editedRole,
        meta_data: {
          ...editedRole.meta_data,
          permissions: {
            ...currentPermissions,
            [category]: {
              ...categoryPerms,
              [action]: value,
            },
          },
        },
      });
    }
  };

  const renderEditablePermissions = () => {
    const currentRole = editing ? editedRole : roleData;
    if (!currentRole) return null;

    return (
      <View style={styles.editablePermissions}>
        {/* Boolean Permissions */}
        <ThemedText style={styles.permissionSectionTitle}>System Access</ThemedText>
        {BOOLEAN_PERMISSIONS.map((permission) => {
          const isEnabled = currentRole.meta_data?.permissions?.[permission.key] === true;
          const { label, originalLabel } = getPermissionLabel(permission, displayNames);

          return (
            <View key={permission.key} style={[styles.permissionCategory, { borderBottomColor: colors.border }]}>
              <View style={styles.permissionEditHeader}>
                <View style={styles.permissionInfo}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <ThemedText style={styles.permissionTitle}>{label}</ThemedText>
                    {originalLabel && (
                      <ThemedText style={{ fontSize: 11, opacity: 0.4, fontStyle: "italic" }}>
                        ({originalLabel})
                      </ThemedText>
                    )}
                  </View>
                  <ThemedText style={styles.permissionDescription}>{permission.description}</ThemedText>
                </View>
                <TouchableOpacity
                  style={[
                    styles.toggleSwitch,
                    {
                      backgroundColor: isEnabled ? colors.primary : colors.border,
                      opacity: editing ? 1 : 0.6,
                    },
                  ]}
                  onPress={() => editing && handlePermissionToggle(permission.key, "boolean", !isEnabled)}
                  disabled={!editing}
                >
                  <View
                    style={[
                      styles.toggleThumb,
                      {
                        backgroundColor: colors.card,
                        transform: [{ translateX: isEnabled ? 20 : 2 }],
                      },
                    ]}
                  />
                </TouchableOpacity>
              </View>
            </View>
          );
        })}

        {/* Entity Management (CRUD and Update-Only Permissions) */}
        <ThemedText style={[styles.permissionSectionTitle, { marginTop: 16 }]}>Entity Management</ThemedText>
        {(() => {
          // Group permissions: parents first, then their children
          const parentPerms = CRUD_PERMISSION_CATEGORIES.filter((p) => !p.childOf);
          const childPerms = CRUD_PERMISSION_CATEGORIES.filter((p) => p.childOf);

          // Build ordered list with children following their parents
          const orderedPerms: PermissionMeta[] = [];
          parentPerms.forEach((parent) => {
            orderedPerms.push(parent);
            childPerms.filter((child) => child.childOf === parent.key).forEach((child) => orderedPerms.push(child));
          });

          return orderedPerms.map((category) => {
            const categoryPerms = currentRole.meta_data?.permissions?.[category.key] || {};
            const isChild = !!category.childOf;
            const { label, originalLabel } = getPermissionLabel(category, displayNames);

            return (
              <View
                key={category.key}
                style={[
                  styles.permissionCategory,
                  { borderBottomColor: colors.border },
                  isChild && { marginLeft: 24, borderLeftWidth: 2, borderLeftColor: colors.border, paddingLeft: 12 },
                ]}
              >
                <View style={styles.permissionEditHeader}>
                  <View style={styles.permissionInfo}>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                      <ThemedText style={[styles.permissionTitle, isChild && { fontSize: 14 }]}>{label}</ThemedText>
                      {originalLabel && (
                        <ThemedText style={{ fontSize: 11, opacity: 0.4, fontStyle: "italic" }}>
                          ({originalLabel})
                        </ThemedText>
                      )}
                    </View>
                  </View>
                  <View style={styles.permissionActions}>
                    {["read", "create", "update", "delete"].map((action) => (
                      <TouchableOpacity
                        key={action}
                        style={[
                          styles.permissionChip,
                          {
                            backgroundColor: categoryPerms[action] ? colors.primary + "20" : colors.backgroundSecondary,
                            borderColor: categoryPerms[action] ? colors.primary : colors.border,
                            opacity: editing ? 1 : 0.6,
                          },
                        ]}
                        onPress={() => editing && handlePermissionToggle(category.key, action, !categoryPerms[action])}
                        disabled={!editing}
                      >
                        <ThemedText
                          style={[
                            styles.permissionChipText,
                            { color: categoryPerms[action] ? colors.primary : colors.text },
                          ]}
                        >
                          {action}
                        </ThemedText>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              </View>
            );
          });
        })()}

        {/* Update-Only Permissions (tenant, integrations) */}
        {UPDATE_ONLY_PERMISSIONS.map((permission) => {
          const categoryPerms = currentRole.meta_data?.permissions?.[permission.key] || {};
          const hasUpdatePermission = categoryPerms.update === true;
          const { label, originalLabel } = getPermissionLabel(permission, displayNames);

          return (
            <View key={permission.key} style={[styles.permissionCategory, { borderBottomColor: colors.border }]}>
              <View style={styles.permissionEditHeader}>
                <View style={styles.permissionInfo}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <ThemedText style={styles.permissionTitle}>{label}</ThemedText>
                    {originalLabel && (
                      <ThemedText style={{ fontSize: 11, opacity: 0.4, fontStyle: "italic" }}>
                        ({originalLabel})
                      </ThemedText>
                    )}
                  </View>
                </View>
                <View style={styles.permissionActions}>
                  <TouchableOpacity
                    style={[
                      styles.permissionChip,
                      {
                        backgroundColor: hasUpdatePermission ? colors.primary + "20" : colors.backgroundSecondary,
                        borderColor: hasUpdatePermission ? colors.primary : colors.border,
                        opacity: editing ? 1 : 0.6,
                      },
                    ]}
                    onPress={() => editing && handlePermissionToggle(permission.key, "update", !hasUpdatePermission)}
                    disabled={!editing}
                  >
                    <ThemedText
                      style={[styles.permissionChipText, { color: hasUpdatePermission ? colors.primary : colors.text }]}
                    >
                      update
                    </ThemedText>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          );
        })}
      </View>
    );
  };

  const handleDelete = () => {
    if (!roleData?.id) return;

    openModal(
      <ConfirmModal
        title="Delete Role"
        message={`Are you sure you want to delete "${roleData.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={async () => {
          closeModal();
          try {
            await roleService.deleteRolesByIds([roleData.id]);
            toast.showToast({
              title: "Role deleted",
              message: "Role has been successfully deleted",
              toastType: "success",
            });
            router.push("/(app)/settings/roles");
          } catch (error: any) {
            toast.showToast({
              title: "Delete failed",
              message: error?.message || "Failed to delete role",
              toastType: "error",
            });
          }
        }}
        onCancel={closeModal}
      />,
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <ThemedText>Loading role data...</ThemedText>
      </View>
    );
  }

  if (!roleData) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.dangerText} />
        <ThemedText style={{ marginTop: 12 }}>Role not found</ThemedText>
        <Button text="Go Back" onPress={() => router.push("/(app)/settings/roles")} style={{ marginTop: 16 }} />
      </View>
    );
  }

  const userCount = users.length;
  const permissionCount = roleData.meta_data?.permissions ? Object.keys(roleData.meta_data.permissions).length : 0;
  const phoneCount = (editing ? editedRole : roleData)?.meta_data?.permissions?.phones?.length || 0;

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <TouchableOpacity onPress={() => router.push("/(app)/settings/roles")} style={styles.backButton}>
              <MaterialCommunityIcons name="arrow-left" size={24} color={colors.text} />
            </TouchableOpacity>
            <View style={styles.headerInfo}>
              <ThemedText type="title">{roleData.name}</ThemedText>
              {roleData.description && (
                <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>{roleData.description}</ThemedText>
              )}
            </View>
          </View>

          <View style={styles.headerActions}>
            {editing ? (
              <>
                <Button
                  text="Cancel"
                  onPress={handleCancel}
                  style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
                  textStyle={{ color: colors.text }}
                />
                <Button
                  text={saving ? "Saving..." : "Save"}
                  onPress={handleSave}
                  disabled={saving}
                  style={[styles.actionButton, { backgroundColor: colors.primary }]}
                  textStyle={{ color: "white" }}
                />
              </>
            ) : (
              <>
                {permissions.update && (
                  <Button
                    text="Edit"
                    iconPosition="right"
                    icon="pencil"
                    onPress={() => setEditing(true)}
                    style={[styles.actionButton, { backgroundColor: colors.primary }]}
                    textStyle={{ color: "white" }}
                  />
                )}
                {permissions.delete && (
                  <Button
                    text="Delete"
                    icon="delete"
                    iconPosition="right"
                    onPress={handleDelete}
                    style={[styles.actionButton, { backgroundColor: colors.red.background }]}
                    textStyle={{ color: "white" }}
                  />
                )}
              </>
            )}
          </View>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={[styles.tabNav, { borderBottomColor: colors.border }]}>
        {[
          { key: "overview", label: "Overview", icon: "information-outline" },
          { key: "users", label: `Users (${userCount})`, icon: "account-group" },
          { key: "permissions", label: `Permissions (${permissionCount})`, icon: "shield-check" },
          { key: "phones", label: `Phones (${phoneCount})`, icon: "phone" },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, { borderBottomColor: activeTab === tab.key ? colors.primary : "transparent" }]}
            onPress={() => setActiveTab(tab.key as any)}
          >
            <MaterialCommunityIcons
              name={tab.icon as any}
              size={20}
              color={activeTab === tab.key ? colors.primary : colors.textSecondary}
            />
            <ThemedText
              style={[styles.tabText, { color: activeTab === tab.key ? colors.primary : colors.textSecondary }]}
            >
              {tab.label}
            </ThemedText>
          </TouchableOpacity>
        ))}
      </View>

      {/* Tab Content */}
      <ScrollView style={styles.tabContent}>
        {activeTab === "overview" && (
          <View style={styles.section}>
            {editing && editedRole ? (
              <>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Role Name</ThemedText>
                  <ThemedInput
                    value={editedRole.name}
                    onChangeText={(text) => setEditedRole({ ...editedRole, name: text })}
                    style={styles.input}
                  />
                </View>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Description</ThemedText>
                  <ThemedInput
                    value={editedRole.description || ""}
                    onChangeText={(text) => setEditedRole({ ...editedRole, description: text })}
                    multiline
                    numberOfLines={3}
                    style={styles.input}
                  />
                </View>
                <View style={styles.toggleRow}>
                  <View style={styles.toggleInfo}>
                    <ThemedText style={styles.label}>Enroll Users For Shifts</ThemedText>
                    <ThemedText style={styles.toggleDescription}>
                      Users with this role will be available for shift assignments and scheduling
                    </ThemedText>
                  </View>
                  <TouchableOpacity
                    style={[
                      styles.toggleSwitch,
                      {
                        backgroundColor: editedRole.meta_data?.accept_shifts ? colors.primary : colors.border,
                      },
                    ]}
                    onPress={() => {
                      const newAcceptShifts = !editedRole.meta_data?.accept_shifts;
                      setEditedRole({
                        ...editedRole,
                        meta_data: {
                          ...editedRole.meta_data,
                          accept_shifts: newAcceptShifts,
                          // Set priority to 1 when enabling accept_shifts if not already set
                          ...(newAcceptShifts && !editedRole.meta_data?.priority ? { priority: 1 } : {}),
                        },
                      });
                    }}
                  >
                    <View
                      style={[
                        styles.toggleThumb,
                        {
                          backgroundColor: colors.card,
                          transform: [{ translateX: editedRole.meta_data?.accept_shifts ? 20 : 2 }],
                        },
                      ]}
                    />
                  </TouchableOpacity>
                </View>
                {editedRole.meta_data?.accept_shifts && (
                  <View style={styles.inputGroup}>
                    <ThemedText style={styles.label}>Priority (1-10)</ThemedText>
                    <ThemedInput
                      value={String(editedRole.meta_data?.priority ?? 1)}
                      onChangeText={(text) => {
                        const num = parseInt(text, 10);
                        const priority = isNaN(num) ? 1 : Math.max(1, Math.min(10, num));
                        setEditedRole({
                          ...editedRole,
                          meta_data: {
                            ...editedRole.meta_data,
                            priority,
                          },
                        });
                      }}
                      keyboardType="numeric"
                      style={styles.input}
                    />
                  </View>
                )}
              </>
            ) : (
              <>
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Name</ThemedText>
                  <ThemedText>{roleData.name}</ThemedText>
                </View>
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Description</ThemedText>
                  <ThemedText>{roleData.description || "No description"}</ThemedText>
                </View>
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Enroll Users For Shifts</ThemedText>
                  <ThemedText>{roleData.meta_data?.accept_shifts ? "Yes" : "No"}</ThemedText>
                </View>
                {roleData.meta_data?.accept_shifts && (
                  <View style={styles.infoRow}>
                    <ThemedText style={styles.infoLabel}>Priority</ThemedText>
                    <ThemedText>{roleData.meta_data?.priority ?? 1}</ThemedText>
                  </View>
                )}
              </>
            )}
          </View>
        )}

        {activeTab === "users" && (
          <View style={styles.section}>
            {editing && (
              <Button
                text="Select Users"
                onPress={() => setShowUserPicker(true)}
                style={styles.pickerButton}
                icon="account-multiple"
              />
            )}
            {users.length > 0 ? (
              <View style={styles.userList}>
                {users.map((user) => {
                  const displayName = user.meta_data?.preferred_name || user.first_name;
                  const fullName = `${displayName} ${user.last_name}`.trim();

                  return (
                    <View key={user.id} style={[styles.userItem, { borderBottomColor: colors.border }]}>
                      <View style={styles.userInfo}>
                        <ThemedText type="defaultSemiBold">{fullName || "Unknown User"}</ThemedText>
                        {user.email_address && <ThemedText style={styles.userDetail}>{user.email_address}</ThemedText>}
                        {user.meta_data?.roles && user.meta_data.roles.length > 0 && (
                          <ThemedText style={styles.userRole}>Roles: {user.meta_data.roles.join(", ")}</ThemedText>
                        )}
                      </View>
                      {editing && (
                        <TouchableOpacity
                          onPress={() => {
                            const updatedUsers = users.filter((u) => u.id !== user.id);
                            setUsers(updatedUsers);
                          }}
                          style={styles.removeButton}
                        >
                          <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : (
              <ThemedText style={{ opacity: 0.5 }}>No users assigned</ThemedText>
            )}
          </View>
        )}

        {activeTab === "permissions" && (
          <View style={styles.section}>
            {roleData.meta_data?.permissions && Object.keys(roleData.meta_data.permissions).length > 0 ? (
              <View style={styles.permissionsContainer}>{renderEditablePermissions()}</View>
            ) : (
              <ThemedText style={{ opacity: 0.5 }}>No permissions configured</ThemedText>
            )}
          </View>
        )}

        {activeTab === "phones" && (
          <View style={styles.section}>
            {twilioConfigured === null ? (
              <ThemedText style={{ opacity: 0.5, textAlign: "center", paddingVertical: 32 }}>
                Checking phone configuration...
              </ThemedText>
            ) : !twilioConfigured ? (
              <View style={styles.notConfiguredContainer}>
                <MaterialCommunityIcons name="phone-off" size={48} color={colors.textSecondary} />
                <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
                  Twilio Not Configured
                </ThemedText>
                <ThemedText style={{ opacity: 0.6, textAlign: "center", marginTop: 8 }}>
                  Phone assignment requires Twilio integration to be configured.
                </ThemedText>
                <ThemedText style={{ opacity: 0.4, textAlign: "center", marginTop: 4, fontSize: 12 }}>
                  Go to Settings → Integrations to configure Twilio.
                </ThemedText>
              </View>
            ) : (
              <>
                {editing && (
                  <Button
                    text="Select Phones"
                    onPress={() => setShowPhonePicker(true)}
                    style={styles.pickerButton}
                    icon="phone"
                  />
                )}
                {(() => {
                  const currentRole = editing ? editedRole : roleData;
                  return currentRole?.meta_data?.permissions?.phones &&
                    currentRole.meta_data.permissions.phones.length > 0 ? (
                    <View style={styles.formGrid}>
                      {currentRole.meta_data.permissions.phones.map((phone, index) => (
                        <View
                          key={phone.number}
                          style={[styles.card, { borderColor: colors.border, backgroundColor: colors.card }]}
                        >
                          {editing ? (
                            <>
                              <View style={styles.row}>
                                <View style={{ flex: 1 }}>
                                  <ThemedText style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                                    Phone Number
                                  </ThemedText>
                                  <ThemedText type="defaultSemiBold">{phone.number}</ThemedText>
                                </View>
                                <Button
                                  icon="delete"
                                  onPress={() => {
                                    const currentRole = editedRole || roleData;
                                    const phones = currentRole.meta_data?.permissions?.phones || [];
                                    const updatedPhones = phones.filter((_, i) => i !== index);
                                    setEditedRole({
                                      ...currentRole,
                                      meta_data: {
                                        ...currentRole.meta_data,
                                        permissions: {
                                          ...currentRole.meta_data?.permissions,
                                          phones: updatedPhones,
                                        },
                                      },
                                    });
                                  }}
                                  style={[styles.iconBtn, { backgroundColor: colors.red.background }]}
                                />
                              </View>
                              <ThemedInput
                                label="Alias (optional)"
                                value={phone.alias || ""}
                                onChangeText={(v) => {
                                  const currentRole = editedRole || roleData;
                                  const phones = currentRole.meta_data?.permissions?.phones || [];
                                  const updatedPhones = [...phones];
                                  updatedPhones[index] = { ...updatedPhones[index], alias: v };
                                  setEditedRole({
                                    ...currentRole,
                                    meta_data: {
                                      ...currentRole.meta_data,
                                      permissions: {
                                        ...currentRole.meta_data?.permissions,
                                        phones: updatedPhones,
                                      },
                                    },
                                  });
                                }}
                                placeholder="e.g., Main Office Line"
                                style={{ marginBottom: 8 }}
                              />
                              <View style={styles.row}>
                                <ThemedText style={{ flex: 1, opacity: 0.7 }}>
                                  Hide Number {!phone.alias ? "(requires alias)" : ""}
                                </ThemedText>
                                <TouchableOpacity
                                  style={[
                                    styles.toggleSwitch,
                                    {
                                      backgroundColor: !phone.show_number ? colors.primary : colors.border,
                                      opacity: !phone.alias ? 0.5 : 1,
                                    },
                                  ]}
                                  onPress={() => {
                                    if (!phone.alias) return; // Don't allow toggling if no alias
                                    const currentRole = editedRole || roleData;
                                    const phones = currentRole.meta_data?.permissions?.phones || [];
                                    const updatedPhones = [...phones];
                                    updatedPhones[index] = { ...updatedPhones[index], show_number: !phone.show_number };
                                    setEditedRole({
                                      ...currentRole,
                                      meta_data: {
                                        ...currentRole.meta_data,
                                        permissions: {
                                          ...currentRole.meta_data?.permissions,
                                          phones: updatedPhones,
                                        },
                                      },
                                    });
                                  }}
                                  disabled={!phone.alias}
                                >
                                  <View
                                    style={[
                                      styles.toggleThumb,
                                      {
                                        backgroundColor: colors.card,
                                        transform: [{ translateX: phone.show_number ? 20 : 2 }],
                                      },
                                    ]}
                                  />
                                </TouchableOpacity>
                              </View>
                            </>
                          ) : (
                            <>
                              <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                                {phone.alias || "Untitled"}
                              </ThemedText>
                              {(phone.show_number || !phone.alias) && (
                                <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>{phone.number}</ThemedText>
                              )}
                              {phone.alias && !phone.show_number && (
                                <ThemedText style={{ opacity: 0.5, marginTop: 4, fontSize: 12 }}>
                                  Number hidden
                                </ThemedText>
                              )}
                            </>
                          )}
                        </View>
                      ))}
                    </View>
                  ) : (
                    <ThemedText style={{ opacity: 0.5 }}>No phones assigned</ThemedText>
                  );
                })()}
              </>
            )}
          </View>
        )}
      </ScrollView>

      {/* User Picker Modal */}
      {showUserPicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <UserPicker
              multiple
              selectedUserIds={users.map((u) => u.id!)}
              onMultiPick={(selectedUsers) => {
                setUsers(selectedUsers);
                setShowUserPicker(false);
              }}
              onCancel={() => setShowUserPicker(false)}
            />
          </View>
        </View>
      )}

      {/* Phone Picker Modal */}
      {showPhonePicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <PhonePicker
              pickMany
              selectedNumbers={editedRole?.meta_data?.permissions?.phones?.map((p: any) => p.number) || []}
              onPick={(selectedNumbers) => {
                if (editedRole) {
                  const currentPhones = editedRole.meta_data?.permissions?.phones || [];
                  const newPhones = selectedNumbers.map((number) => {
                    // Check if phone already exists
                    const existingPhone = currentPhones.find((p: any) => p.number === number);
                    if (existingPhone) {
                      return existingPhone;
                    }
                    // Create new phone object
                    return {
                      number,
                      alias: "",
                      show_number: true,
                    };
                  });
                  setEditedRole({
                    ...editedRole,
                    meta_data: {
                      ...editedRole.meta_data,
                      permissions: {
                        ...editedRole.meta_data?.permissions,
                        phones: newPhones,
                      },
                    },
                  });
                }
                setShowPhonePicker(false);
              }}
              onCancel={() => setShowPhonePicker(false)}
            />
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  header: {
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  headerInfo: {
    flex: 1,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  tabNav: {
    flexDirection: "row",
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "500",
  },
  tabContent: {
    flex: 1,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  notConfiguredContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
    gap: 4,
  },
  inputGroup: {
    gap: 8,
  },
  toggleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
  },
  toggleInfo: {
    flex: 1,
    marginRight: 12,
  },
  toggleDescription: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 2,
  },
  label: {
    fontSize: 14,
    opacity: 0.7,
    fontWeight: "500",
  },
  input: {
    marginBottom: 0,
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    alignItems: "center",
  },
  infoLabel: {
    opacity: 0.7,
    flex: 1,
  },
  pickerButton: {
    alignSelf: "flex-start",
    marginBottom: 16,
  },
  itemGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  userList: {
    gap: 0,
  },
  userItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  userInfo: {
    flex: 1,
    gap: 2,
  },
  userDetail: {
    fontSize: 12,
    opacity: 0.6,
  },
  userRole: {
    fontSize: 11,
    opacity: 0.5,
    fontStyle: "italic",
  },
  removeButton: {
    padding: 4,
  },
  permissionsContainer: {
    gap: 8,
  },
  editablePermissions: {
    gap: 8,
  },
  permissionSectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 8,
    opacity: 0.8,
  },
  permissionCategory: {
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  permissionEditHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  permissionInfo: {
    flex: 1,
    marginRight: 12,
  },
  permissionTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  permissionDescription: {
    fontSize: 14,
    opacity: 0.7,
    marginTop: 2,
  },
  toggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    padding: 2,
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    boxShadow: "0 2px 2.22px 0 rgba(0, 0, 0, 0.22)",
    elevation: 3,
  },
  permissionActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  permissionChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
  },
  permissionChipText: {
    fontSize: 12,
    fontWeight: "500",
    textTransform: "capitalize",
  },
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    width: "90%",
    maxWidth: 500,
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  formGrid: {
    gap: 12,
  },
  card: {
    padding: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    gap: 8,
  },
  row: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
  },
  iconBtn: {
    width: 40,
    height: 40,
    backgroundColor: "transparent",
  },
});
